import {
  getDocs,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import {
  auditedAddDoc as addDoc,
  auditedUpdateDoc as updateDoc,
  auditedDeleteDoc as deleteDoc,
} from './auditedWrites';
import { getEffectiveTenantId, tenantCollection, tenantDoc } from './tenantContext';

const HOLD_COL = 'programHoldReasons';
const DISCHARGE_COL = 'programDischargeReasons';
const CLOSE_COL = 'programCloseReasons';

/**
 * Default close reasons, seeded (admin only) when the collection is empty.
 * Shared by both "Close — Remove from Outreach" and "Close — Remove from
 * Pre-Service"; the closed-from stage is recorded on the session separately.
 */
export const DEFAULT_CLOSE_REASONS = [
  { name: 'Unable to contact', sortOrder: 10, noteRequired: false, active: true },
  { name: 'Patient declined', sortOrder: 20, noteRequired: false, active: true },
  { name: 'Not a candidate', sortOrder: 30, noteRequired: false, active: true },
  { name: 'Insurance/coverage issue', sortOrder: 40, noteRequired: false, active: true },
  { name: 'Other (specify)', sortOrder: 999, noteRequired: true, active: true },
];

async function fetchAll(tenantId, collectionName) {
  if (!tenantId) return [];
  let snap;
  try {
    snap = await getDocs(query(tenantCollection(tenantId, collectionName), orderBy('sortOrder', 'asc')));
  } catch {
    snap = await getDocs(tenantCollection(tenantId, collectionName));
  }
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  rows.sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0));
  return rows;
}

export async function getHoldReasons() {
  const tenantId = await getEffectiveTenantId();
  return fetchAll(tenantId, HOLD_COL);
}

export async function getActiveHoldReasons() {
  const all = await getHoldReasons();
  return all.filter((r) => r.active !== false);
}

export async function addHoldReason({ name, sortOrder, active }, profile) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) throw new Error('Cannot determine tenant');
  const uid = profile?.id || profile?.uid || null;
  const payload = {
    name: (name || '').trim().slice(0, 200),
    sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 100,
    active: active !== false,
    tenantId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: uid,
    updatedBy: uid,
  };
  if (!payload.name) throw new Error('Reason name is required.');
  const ref = await addDoc(tenantCollection(tenantId, HOLD_COL), payload);
  return ref.id;
}

export async function updateHoldReason(id, patch, profile) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) throw new Error('Cannot determine tenant');
  const uid = profile?.id || profile?.uid || null;
  const out = {};
  if (patch.name != null) out.name = String(patch.name).trim().slice(0, 200);
  if (patch.sortOrder != null) out.sortOrder = Number(patch.sortOrder) || 100;
  if (patch.active != null) out.active = !!patch.active;
  out.updatedAt = serverTimestamp();
  out.updatedBy = uid;
  await updateDoc(tenantDoc(tenantId, HOLD_COL, id), out);
}

export async function deleteHoldReason(id) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) throw new Error('Cannot determine tenant');
  await deleteDoc(tenantDoc(tenantId, HOLD_COL, id));
}

export async function getDischargeReasons() {
  const tenantId = await getEffectiveTenantId();
  return fetchAll(tenantId, DISCHARGE_COL);
}

export async function getActiveDischargeReasons() {
  const all = await getDischargeReasons();
  return all.filter((r) => r.active !== false);
}

export async function addDischargeReason({ name, sortOrder, active }, profile) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) throw new Error('Cannot determine tenant');
  const uid = profile?.id || profile?.uid || null;
  const payload = {
    name: (name || '').trim().slice(0, 200),
    sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 100,
    active: active !== false,
    tenantId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: uid,
    updatedBy: uid,
  };
  if (!payload.name) throw new Error('Reason name is required.');
  const ref = await addDoc(tenantCollection(tenantId, DISCHARGE_COL), payload);
  return ref.id;
}

export async function updateDischargeReason(id, patch, profile) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) throw new Error('Cannot determine tenant');
  const uid = profile?.id || profile?.uid || null;
  const out = {};
  if (patch.name != null) out.name = String(patch.name).trim().slice(0, 200);
  if (patch.sortOrder != null) out.sortOrder = Number(patch.sortOrder) || 100;
  if (patch.active != null) out.active = !!patch.active;
  out.updatedAt = serverTimestamp();
  out.updatedBy = uid;
  await updateDoc(tenantDoc(tenantId, DISCHARGE_COL, id), out);
}

export async function deleteDischargeReason(id) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) throw new Error('Cannot determine tenant');
  await deleteDoc(tenantDoc(tenantId, DISCHARGE_COL, id));
}

export async function getCloseReasons() {
  const tenantId = await getEffectiveTenantId();
  return fetchAll(tenantId, CLOSE_COL);
}

export async function getActiveCloseReasons() {
  const all = await getCloseReasons();
  return all.filter((r) => r.active !== false);
}

/**
 * Loads close reasons; if the collection is empty for this tenant, seeds
 * defaults (admin only — writes require isTenantAdmin). Returns { rows, seeded }.
 */
export async function getCloseReasonsWithSeed(profile) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) return { rows: [], seeded: false };
  const existing = await fetchAll(tenantId, CLOSE_COL);
  if (existing.length > 0) return { rows: existing, seeded: false };
  for (const r of DEFAULT_CLOSE_REASONS) {
    await addCloseReason(r, profile);
  }
  return { rows: await fetchAll(tenantId, CLOSE_COL), seeded: true };
}

export async function addCloseReason({ name, sortOrder, noteRequired, active }, profile) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) throw new Error('Cannot determine tenant');
  const uid = profile?.id || profile?.uid || null;
  const payload = {
    name: (name || '').trim().slice(0, 200),
    sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 100,
    noteRequired: !!noteRequired,
    active: active !== false,
    tenantId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: uid,
    updatedBy: uid,
  };
  if (!payload.name) throw new Error('Reason name is required.');
  const ref = await addDoc(tenantCollection(tenantId, CLOSE_COL), payload);
  return ref.id;
}

export async function updateCloseReason(id, patch, profile) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) throw new Error('Cannot determine tenant');
  const uid = profile?.id || profile?.uid || null;
  const out = {};
  if (patch.name != null) out.name = String(patch.name).trim().slice(0, 200);
  if (patch.sortOrder != null) out.sortOrder = Number(patch.sortOrder) || 100;
  if (patch.noteRequired != null) out.noteRequired = !!patch.noteRequired;
  if (patch.active != null) out.active = !!patch.active;
  out.updatedAt = serverTimestamp();
  out.updatedBy = uid;
  await updateDoc(tenantDoc(tenantId, CLOSE_COL, id), out);
}

export async function deleteCloseReason(id) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) throw new Error('Cannot determine tenant');
  await deleteDoc(tenantDoc(tenantId, CLOSE_COL, id));
}

export const PROVIDER_CHANGE_REASON_NAME = 'Provider change';

/**
 * Resolve the discharge reason to record when a provider change auto-discharges
 * program sessions. Returns an existing active reason named "Provider change"
 * (reads are allowed for all care users); otherwise falls back to a name-only
 * reason with `id: null`. Never writes to `programDischargeReasons` (that
 * collection is admin-only), so it works for every role.
 * @returns {Promise<{ id: string|null, name: string }>}
 */
export async function resolveProviderChangeDischargeReason() {
  try {
    const active = await getActiveDischargeReasons();
    const match = active.find(
      (r) => (r.name || '').trim().toLowerCase() === PROVIDER_CHANGE_REASON_NAME.toLowerCase(),
    );
    if (match) return { id: match.id, name: match.name };
  } catch {
    // fall through to name-only
  }
  return { id: null, name: PROVIDER_CHANGE_REASON_NAME };
}
